import React, { forwardRef, useEffect, useState } from "react";
import logo from "../../Assets/Logos/ashok1.png";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { FaCarSide, FaTruckMoving } from "react-icons/fa6";
import { RiMotorbikeFill } from "react-icons/ri";
import { useAssets } from "../../Context/AssetsContext";

const LicenseCard = forwardRef(({ data }, ref) => {
  const { assets } = useAssets();
  const [profileImgSrc, setProfileImgSrc] = useState("");
  const [signatureImgSrc, setSignatureImgSrc] = useState("");

  const iconMap = {
    car: FaCarSide,
    bike: RiMotorbikeFill,
    truck: FaTruckMoving,
  };

  const vehicleOrder = ["MCWG", "LMV"];
  let vehicles = data?.vehicles || [];
  vehicles = [
    ...vehicles.filter((v) => vehicleOrder.includes(v.code)),
    ...vehicles.filter((v) => !vehicleOrder.includes(v.code)),
  ];

  const CircleBadge = ({
    text,
    bgColor,
    textColor = "#000000",
    diameter = 20,
  }) => {
    const radius = diameter / 2;
    const scaledFontSize = 8;

    return (
      <svg width={diameter} height={diameter} className="inline-block">
        <circle
          cx={radius}
          cy={radius}
          r={radius - 1}
          fill={bgColor}
          stroke="#000000"
          strokeWidth="1"
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="middle"
          fill={textColor}
          fontSize={scaledFontSize}
          fontWeight="bold"
        >
          {text}
        </text>
      </svg>
    );
  };

  const dlId = data.dlId || "";
  const stateBadgeText = dlId.slice(0, 2) === "OR" ? "OD" : dlId.slice(0, 2);

  useEffect(() => {
    // Set profile image
    setProfileImgSrc(
      data.profileImage.startsWith("data:image")
        ? data.profileImage
        : `data:image/jpeg;base64,${data.profileImage}`,
    );

    // ✅ Set signature image - handles Cloudinary URLs, data URIs, and base64
    if (data.signatureImage) {
      if (data.signatureImage.startsWith("http")) {
        setSignatureImgSrc(data.signatureImage);
      } else if (data.signatureImage.startsWith("data:image")) {
        setSignatureImgSrc(data.signatureImage);
      } else {
        setSignatureImgSrc(`data:image/png;base64,${data.signatureImage}`);
      }
    } else {
      setSignatureImgSrc("");
    }
  }, [data.profileImage, data.signatureImage]);

  return (
    <div
      ref={ref}
      className="license-card flex flex-col md:flex-row gap-2 p-1 tracking-normal"
      style={{
        width: "100%",
        maxWidth: "700px",
        backgroundColor: "white",
        transform: "scale(0.85)",
        transformOrigin: "top center",
      }}
    >
      {/* Front Card */}
      <div
        className="border border-black rounded shadow p-1 flex flex-col justify-between"
        style={{ width: "340px", height: "215px" }}
      >
        <div className="flex flex-row justify-between items-center  relative">
          <img src={assets.ashok1 || logo} alt="Emblem" className="w-10 h-16" />
          <div className="absolute  -top-1 text-center left-16">
            <h2 className="text-[12px] font-extrabold text-center">
              INDIAN UNION DRIVING LICENCE
            </h2>
            <p className="text-[12px]  font-extrabold  text-start ml-5 uppercase">
              ISSUED BY {data.state || "  "}
            </p>
            <p className="text-[10px] font-extrabold text-start  ml-3">
              {(data.dlId || " ").length > 4
                ? data.dlId.slice(0, 4) + " " + data.dlId.slice(4)
                : data.dlId}
            </p>
          </div>
          <div></div>
          <div className="  relative">
            <div className=" absolute -top-9 right-2">
              <CircleBadge text={stateBadgeText} bgColor="#fb923c" />
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="text-center absolute -top-24 right-4">
            {/* ✅ Blank box shown when image fails to load — no fallback image */}
            {profileImgSrc ? (
              <img
                src={profileImgSrc}
                alt="Holder"
                className="w-16 h-16 border object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="w-16 h-16 border bg-gray-100"
              style={{
                display: profileImgSrc ? "none" : "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
            <p className="text-[6px] mt-2 font-bold">Holder's Signature</p>
          </div>

          <div className="flex flex-col justify-between relative   ">
            <div className="absolute left-20 -top-20">
              <div className="text-[8px] flex flex-row justify-between gap-1 font-extrabold ">
                <div className="flex flex-col  ">
                  <p>
                    <strong>Issue Date</strong>
                  </p>
                  <p>{data.issueDate === null ? "  " : data.issueDate}</p>
                </div>
                <div className="flex flex-col ">
                  <p>
                    <strong>Validity (NT)</strong>
                  </p>
                  <p>{data.validNT === null ? "  " : data.validNT}</p>
                </div>{" "}
                <div className="flex flex-col ">
                  <p>
                    <strong>Validity (TR)</strong>
                  </p>
                  <p>{data.validTR === null ? "  " : data.validTR}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="text-[8px] absolute -top-[6rem] left-1 font-extrabold">
            {" "}
            <div className="flex flex-row gap-2">
              <p className="w-[3rem]">
                <strong>Name </strong>
              </p>
              <p>:{data.name || " "}</p>
            </div>
            <div className="flex flex-row gap-1">
              <p>
                <strong>Date of Birth</strong> :{data.dob || " "}
              </p>
              <p className="">
                <strong>Blood Group</strong> :{" "}
                {data.bloodGroup === "Not specified" || !data.bloodGroup
                  ? "Unknown"
                  : data.bloodGroup}
              </p>

              <p className="absolute  whitespace-nowrap ml-[13.5rem]">
                <strong className=" whitespace-nowrap">Organ Donor</strong> :
                {data.doner || "No"}
              </p>
            </div>
            <p>
              <strong>Son/Daughter/Wife of</strong> :{data.fatherName || " "}
            </p>
            <p className="underline ">Present Address</p>
            <p className=" w-[50%] absolute ">{data.address || " "}</p>
          </div>
          <div className="relative">
            <div className="text-right text-[7px] absolute  -right-10 -top-24 -rotate-90 tracking-tighter font-bold">
              Date Of First Issue : {data.issueDate || " "}
            </div>
          </div>
        </div>
      </div>

      {/* Back Card */}
      <div
        className="border border-black rounded shadow px-1 py-1 flex flex-col justify-between font-bold"
        style={{ width: "340px", height: "215px" }}
      >
        <div className="text-[9px] relative ">
          <div className="flex flex-row gap-2  absolute -top-1">
            <p className="font-bold ">DL No.</p>
            <p className="font-bold text-[10px]">
              {(data.dlId || " ").length > 4
                ? data.dlId.slice(0, 4) + " " + data.dlId.slice(4)
                : data.dlId}
            </p>
          </div>
        </div>
        <div className="flex justify-between items-center relative">
          <div className="absolute -top-4 w-full">
            <div className="items-start ml-4 mt-0 ">
              <div style={{ width: "40px", height: "40px" }}>
                <QRCodeSVG
                  value={`name:${data.name}, DOB:${data.dob}, DL No:${data.dlId}`}
                  level="H"
                  size={200}
                  style={{ width: "100%", height: "100%" }}
                />
              </div>
            </div>
            <div className=" ">
              <div className="text-[10px] font-bold  relative  ">
                <div className="flex flex-row justify-between pb-4 mt-0 absolute -top-12 left-20">
                  <p>ADPVEH No.(Regn.Numbers) :</p>
                  <br />
                </div>
                <div className="flex  flex-row absolute -top-5 left-20 gap-6 ">
                  <p>Hazardous Validity</p>
                  <p>Hill Validity</p>
                </div>
              </div>
              <div></div>
            </div>
          </div>
        </div>
        <div className=" relative">
          <div className="absolute -top-0">
            <div className="text-[7px] overflow-auto">
              <table
                style={{ border: "0.5px solid black" }}
                className="thin-border-table w-[98%] border font-bold  text-center"
              >
                <thead>
                  <tr
                    style={{ border: "0.5px solid black" }}
                    className="relative"
                  >
                    <th
                      style={{ border: "0.5px solid black" }}
                      className="border  text-center tracking-tighter leading-tight w-10 pt-0 mt-0 pb-2 "
                    >
                      Class of Vehicle
                    </th>
                    <th
                      style={{ border: "0.5px solid black" }}
                      className="border  text-top tracking-tighter leading-none w-10 pb-2 "
                    >
                      Code
                    </th>
                    <th
                      style={{ border: "0.5px solid black" }}
                      className="border  text-center tracking-tighter leading-none w-10 pb-2 "
                    >
                      Issued by
                    </th>
                    <th
                      style={{ border: "0.5px solid black" }}
                      className="border  text-center tracking-tighter leading-none w-10 mt-0 pb-2"
                    >
                      Date of Issue
                    </th>
                    <th
                      style={{ border: "0.5px solid black" }}
                      className="border  text-start tracking-tighter leading-none w-8 mt-0 pb-2"
                    >
                      Vehicle Category
                    </th>

                    <th
                      style={{ border: "0.5px solid black" }}
                      className="border  text-start tracking-tighter leading-none mt-0 pb-2"
                    >
                      Badge Number
                    </th>
                    <th
                      style={{ border: "0.5px solid black" }}
                      className="border  text-start tracking-tighter leading-none mt-0 pb-2"
                    >
                      Badge issued date
                    </th>
                    <th
                      style={{ border: "0.5px solid black" }}
                      className="border  text-start tracking-tighter leading-none mt-0 pb-2"
                    >
                      Badge Issued By
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.vehicles.map((v, i) => {
                    const Icon = iconMap[v.icon];
                    return (
                      <tr style={{ border: "0.5px solid black" }} key={i}>
                        <td
                          style={{ border: "0.5px solid black" }}
                          className="border     transform scale-x-[-1]"
                        >
                          {Icon ? <Icon className="inline text-xs" /> : v.icon}
                        </td>
                        <td
                          style={{ border: "0.5px solid black" }}
                          className="border    pb-1"
                        >
                          {v.code}
                        </td>
                        <td
                          style={{ border: "0.5px solid black" }}
                          className="border    pb-1"
                        >
                          {data.dlId.slice(0, 4)}
                        </td>
                        <td
                          style={{ border: "0.5px solid black" }}
                          className="border    pb-1"
                        >
                          {v.date}
                        </td>
                        <td
                          style={{ border: "0.5px solid black" }}
                          className="border    pb-1"
                        >
                          {v.cat}
                        </td>
                        <td
                          style={{ border: "0.5px solid black" }}
                          className="border    pb-1"
                        >
                          {v.badge}
                        </td>
                        <td
                          style={{ border: "0.5px solid black" }}
                          className="border    pb-1"
                        >
                          {v.badgeDate}
                        </td>
                        <td
                          style={{ border: "0.5px solid black" }}
                          className="border  "
                        >
                          {v.badgeBy}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <br />
        <div className="flex justify-between font-extrabold text-[7px] mt-1 relative">
          <div className="absolute -bottom-2 font-bold ">
            <p>Emergency Contact Number</p>
          </div>

          {/* ✅ Signature Image Section - Above Licensing Authority */}
          <div className="absolute -bottom-8 right-2">
            {signatureImgSrc && (
              <div className=" mr-4 flex justify-end">
                <img
                  src={signatureImgSrc}
                  alt="Authority Signature"
                  className="h-3 w-auto object-contain"
                  crossOrigin="anonymous"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}
            <p className="font-bold  text-[6px]">Licensing Authority</p>
            <p className="font-extrabold uppercase text-[8px]">
              {data.rto || "  "}
            </p>
          </div>
        </div>
        <div className="relative">
          <div className="text-right text-[6px] absolute  -right-5 -top-24 -rotate-90 tracking-tighter font-bold">
            Form 7 Rule 16(2)
          </div>
        </div>
      </div>
    </div>
  );
});

export default LicenseCard;
